import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  Loader2,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import * as XLSX from 'xlsx'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@radix-ui/react-dialog'
import { DialogFooter, DialogHeader } from './ui/dialog'

interface QuestionRow {
  category?: string
  text: string
  option_a: string
  option_b: string
  option_c?: string
  option_d?: string
  correct_answer: string
}

export default function CSVImportForm() {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [previewData, setPreviewData] = useState<QuestionRow[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [failedQuestions, setFailedQuestions] = useState<
    Array<{ question: string; error: string }>
  >([])
  const [showFailedDialog, setShowFailedDialog] = useState(false)

  const downloadTemplate = () => {
    const rows: string[][] = [
      ['Kategori', 'Soru No', 'Soru', 'A', 'B', 'C', 'D', 'Doğru Cevap'],
      ['Genel Kültür', '1', "Türkiye'nin başkenti neresidir?", 'İstanbul', 'Ankara', 'İzmir', 'Bursa', 'B'],
      ['Coğrafya', '2', 'Amazon Nehri hangi kıtadadır?', 'Afrika', 'Asya', 'Güney Amerika', 'Avustralya', 'C'],
      ['Bilim', '3', 'Dünya güneşin etrafında döner mi?', 'Evet', 'Hayır', '', '', 'A'],
    ]

    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = [
      { wch: 16 }, { wch: 10 }, { wch: 50 },
      { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 },
      { wch: 14 },
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Şablon')
    XLSX.writeFile(wb, 'soru_sablonu.xlsx', { compression: true })
  }

  const normalizeCorrectAnswer = (v: unknown): string | null => {
    if (v == null) return null
    const s = String(v).trim().toUpperCase()
    if (['A', 'B', 'C', 'D'].includes(s)) return s
    return null
  }

  const parseAOA = (aoa: any[][]): { data: QuestionRow[]; errors: string[] } => {
    const errs: string[] = []
    if (!aoa || aoa.length < 2) return { data: [], errors: ['Dosya boş veya sadece başlık var.'] }

    const rawHeaders = (aoa[0] || []).map((h: any) => String(h ?? '').trim())

    // Normalize Turkish header names to internal keys
    const headerMap: Record<string, string> = {
      'kategori': 'category',
      'category': 'category',
      'soru no': 'question_no',
      'soru_no': 'question_no',
      'no': 'question_no',
      'soru': 'text',
      'text': 'text',
      'soru metni': 'text',
      'a': 'option_a',
      'b': 'option_b',
      'c': 'option_c',
      'd': 'option_d',
      'doğru cevap': 'correct_answer',
      'dogru cevap': 'correct_answer',
      'correct_answer': 'correct_answer',
      'cevap': 'correct_answer',
    }

    const headers = rawHeaders.map((h) => headerMap[h.toLocaleLowerCase('tr')] ?? h.toLocaleLowerCase('tr'))

    const required = ['text', 'option_a', 'option_b', 'correct_answer']
    const missing = required.filter((col) => !headers.includes(col))
    if (missing.length) {
      const displayNames: Record<string, string> = {
        text: 'Soru',
        option_a: 'A',
        option_b: 'B',
        correct_answer: 'Doğru Cevap',
      }
      return { data: [], errors: [`Eksik sütunlar: ${missing.map((m) => displayNames[m] || m).join(', ')}`] }
    }

    const idx: Record<string, number> = Object.fromEntries(
      headers.map((h: string, i: number) => [h, i])
    )

    const data: QuestionRow[] = []
    for (let r = 1; r < aoa.length; r++) {
      const row = aoa[r] || []
      if (row.every((x: any) => x == null || String(x).trim() === '')) continue

      const category = (row[idx['category']] ?? '').toString().trim() || undefined
      const text = (row[idx['text']] ?? '').toString().trim()
      const option_a = (row[idx['option_a']] ?? '').toString().trim()
      const option_b = (row[idx['option_b']] ?? '').toString().trim()
      const option_c = idx['option_c'] !== undefined ? (row[idx['option_c']] ?? '').toString().trim() : ''
      const option_d = idx['option_d'] !== undefined ? (row[idx['option_d']] ?? '').toString().trim() : ''
      const correctAnswer = normalizeCorrectAnswer(row[idx['correct_answer']])

      if (!text) { errs.push(`Satır ${r + 1}: Boş soru metni.`); continue }
      if (!option_a) { errs.push(`Satır ${r + 1}: A şıkkı boş.`); continue }
      if (!option_b) { errs.push(`Satır ${r + 1}: B şıkkı boş.`); continue }
      if (!correctAnswer) {
        errs.push(`Satır ${r + 1}: Doğru Cevap A/B/C/D olmalıdır.`)
        continue
      }
      if ((correctAnswer === 'C' && !option_c) || (correctAnswer === 'D' && !option_d)) {
        errs.push(`Satır ${r + 1}: Doğru cevap "${correctAnswer}" ama o şık boş.`)
        continue
      }

      data.push({
        category,
        text,
        option_a,
        option_b,
        option_c: option_c || undefined,
        option_d: option_d || undefined,
        correct_answer: correctAnswer,
      })
    }
    return { data, errors: errs }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast({
        title: 'Geçersiz dosya türü',
        description: 'Lütfen Excel (.xlsx/.xls) dosyası seçin.',
        variant: 'destructive',
      })
      return
    }

    setIsProcessing(true)
    setErrors([])
    setPreviewData([])

    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as any[][]
      const { data, errors: errs } = parseAOA(aoa)
      setPreviewData(data)
      setErrors(errs)

      toast({
        title: errs.length ? 'Parsing uyarıları' : 'Başarılı!',
        description: errs.length ? `${errs.length} hata bulundu.` : `${data.length} soru hazır.`,
        variant: errs.length ? 'destructive' : 'default',
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Dosya okunamadı.'
      setErrors([msg])
      toast({ title: 'Dosya okuma hatası', description: msg, variant: 'destructive' })
    } finally {
      setIsProcessing(false)
    }
  }

  const getChoices = (row: QuestionRow) => {
    const options = [
      { label: 'A', text: row.option_a },
      { label: 'B', text: row.option_b },
      ...(row.option_c ? [{ label: 'C', text: row.option_c }] : []),
      ...(row.option_d ? [{ label: 'D', text: row.option_d }] : []),
    ]
    return options.map((o) => ({ ...o, is_correct: o.label === row.correct_answer }))
  }

  const handleImport = async () => {
    if (!previewData.length) {
      toast({ title: 'Hata', description: 'İçe aktarılacak soru yok.', variant: 'destructive' })
      return
    }

    setIsProcessing(true)
    let successCount = 0
    const failed: Array<{ question: string; error: string }> = []

    for (const row of previewData) {
      const form = new FormData()
      form.append('text', row.text)
      form.append('category', row.category?.trim() || 'Diğer')
      form.append('choices', JSON.stringify(getChoices(row)))

      try {
        const res = await authedFetch(`${API_BASE_URL}/api/questions`, {
          method: 'POST',
          body: form,
        })
        if (res.ok) {
          successCount++
        } else {
          const errorData = await res.text()
          failed.push({
            question: row.text.length > 50 ? row.text.substring(0, 50) + '...' : row.text,
            error: `HTTP ${res.status}: ${errorData || 'Bilinmeyen hata'}`,
          })
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Ağ hatası'
        failed.push({
          question: row.text.length > 50 ? row.text.substring(0, 50) + '...' : row.text,
          error: errorMessage,
        })
      }
    }

    setIsProcessing(false)

    if (successCount === previewData.length) {
      toast({
        title: 'İçe aktarma tamamlandı!',
        description: `${successCount}/${previewData.length} soru eklendi.`,
        variant: 'default',
      })
      fileInputRef.current!.value = ''
      setPreviewData([])
      setErrors([])
    } else {
      setFailedQuestions(failed)
      setShowFailedDialog(true)
    }
  }

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p>
              Excel dosyanızda her soru tek satır olmalıdır. Gerekli sütunlar:{' '}
              <code className="bg-gray-100 px-2 py-1 rounded">
                Kategori, Soru No, Soru, A, B, C, D, Doğru Cevap
              </code>
            </p>
            <div className="text-xs space-y-1 mt-2">
              <p>
                <strong>Notlar:</strong>
              </p>
              <ul className="list-disc list-inside ml-2">
                <li>C ve D şıkları isteğe bağlıdır; boş bırakılabilir</li>
                <li>Doğru Cevap sütununa şık harfi yazılır (A, B, C veya D)</li>
                <li>Soru No sütunu isteğe bağlıdır</li>
              </ul>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <Button variant="outline" onClick={downloadTemplate} className="flex items-center gap-2">
        <Download className="h-4 w-4" /> Şablon İndir
      </Button>

      <Card className="border-2 border-dashed border-gray-300">
        <CardContent className="p-6 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isProcessing}
          />
          <div className="space-y-4">
            <FileText className="h-12 w-12 text-gray-400 mx-auto" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Excel Seç
            </Button>
            <p className="text-sm text-gray-500">Max 10 MB</p>
          </div>
        </CardContent>
      </Card>

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errors.map((e, i) => (
              <div key={i} className="text-sm">
                • {e}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {previewData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" /> Önizleme ({previewData.length} soru)
              </h3>
              <Button onClick={handleImport} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    İşleniyor...
                  </>
                ) : (
                  'Soruları İçe Aktar'
                )}
              </Button>
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {previewData.map((row, idx) => {
                const choices = getChoices(row)
                return (
                  <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-medium flex-1">{row.text}</h4>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full ml-4">
                        {choices.length} şık
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {choices.map((c, i) => (
                        <div key={i} className="flex items-start">
                          <span className="font-semibold mr-2">{c.label})</span>
                          <span className="flex-1">{c.text}</span>
                          {c.is_correct && (
                            <span className="text-green-600 font-bold ml-2">✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {row.category && (
                      <div className="mt-3 pt-2 border-t">
                        <span className="text-xs text-gray-600">Kategori: </span>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                          {row.category}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showFailedDialog} onOpenChange={setShowFailedDialog}>
        <DialogContent className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden z-50">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-600" />
              <div>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  Aktarılamayan Sorular
                </DialogTitle>
                <DialogDescription className="text-gray-600 mt-1">
                  {failedQuestions.length} soru içe aktarılamadı
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-4 overflow-y-auto max-h-[calc(85vh-180px)]">
            {failedQuestions.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">Tüm sorular başarıyla içe aktarıldı.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {failedQuestions.map((f, i) => (
                  <div
                    key={i}
                    className="border border-red-200 bg-red-50 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 bg-red-200 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-red-700">{i + 1}</span>
                      </div>
                      <div className="flex-1 space-y-2">
                        <p className="font-semibold text-red-900 leading-relaxed">{f.question}</p>
                        <p className="text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                          {f.error}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-gray-50">
            <Button
              variant="secondary"
              onClick={() => setShowFailedDialog(false)}
              className="w-full sm:w-auto"
              size="lg"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
